import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { setupVisibilityAndFocusListeners } from '../setupVisibilityAndFocusListener/setupVisibilityAndFocusListener';
import { useApiAbortable } from '../useApi/useApi.hook';

import { DEFAULT_POLLING_OPTIONS } from './usePolling.constants';
import type { PollingOptions, StartPollingArgs } from './usePolling.types';
import { getSleepTime, isAbortError, sleep } from './usePolling.utils';

/**
 * Hook for endpoint polling
 * @param endpoint endpoint to start polling
 * @param pollingOptions polling options
 * @returns
 */

export const usePolling = <T>(
  endpoint: string,
  pollingOptions?: Partial<PollingOptions>,
  apiBaseUrl?: string
) => {
  const options = useMemo(
    () => ({ ...DEFAULT_POLLING_OPTIONS, ...pollingOptions }),
    [pollingOptions]
  );

  const requestsCount = useRef(0);
  const shouldRequestAgain = useRef(false);
  const abortRef = useRef<AbortController>(undefined);
  const [isPolling, setIsPolling] = useState(false);

  const initialPollingArgsRef = useRef<StartPollingArgs<T> | null>(null);

  const { executeRequest, error, ...rest } = useApiAbortable<T>(
    endpoint,
    undefined,
    apiBaseUrl
  );

  const startPolling = useCallback((args: StartPollingArgs<T> = {}) => {
    abortRef.current = new AbortController();

    shouldRequestAgain.current = true;
    requestsCount.current = 0;
    initialPollingArgsRef.current = args;
    setIsPolling(true);
  }, []);

  const stopPolling = useCallback(() => {
    setIsPolling(false);

    shouldRequestAgain.current = false;
    abortRef.current?.abort();
    abortRef.current = undefined;
  }, []);

  const initiateLoop = useCallback(async () => {
    while (!abortRef.current?.signal.aborted) {
      if (!shouldRequestAgain.current) {
        break;
      }

      try {
        requestsCount.current++;
        const { handleResponse, requestOptions } =
          initialPollingArgsRef.current || {};
        const response = await executeRequest({
          ...requestOptions,
          signal: abortRef.current?.signal,
        });

        if (handleResponse) {
          handleResponse(response);
        }
      } catch (error) {
        if (isAbortError(error)) {
          break;
        }

        console.error(error);
      }

      const time = getSleepTime({
        requestsCount: requestsCount.current,
        delayUnit: options.delayUnit,
        maxTime: options.maxTime,
        maxAttemptsBeforeDelay: options.maxAttemptsBeforeDelay,
      });

      if (time) {
        try {
          await sleep(time, abortRef.current?.signal);
        } catch (err) {
          if (isAbortError(err)) {
            break;
          }
        }
      }
    }
  }, [
    executeRequest,
    options.maxAttemptsBeforeDelay,
    options.maxTime,
    options.delayUnit,
  ]);

  const handlePollingResume = useCallback(() => {
    if (initialPollingArgsRef.current && !isPolling) {
      startPolling(initialPollingArgsRef.current ?? {});
    }
  }, [startPolling, isPolling]);

  const handleDocumentVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      handlePollingResume();
    } else {
      stopPolling();
    }
  }, [handlePollingResume, stopPolling]);

  useEffect(() => {
    if (isPolling) {
      initiateLoop();
    }
  }, [isPolling, initiateLoop]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      initialPollingArgsRef.current = null;
    };
  }, []);

  useEffect(() => {
    const cleanup = setupVisibilityAndFocusListeners({
      onFocus: handlePollingResume,
      onVisibilityChange: handleDocumentVisibilityChange,
    });

    return cleanup;
  }, [handlePollingResume, handleDocumentVisibilityChange]);

  return {
    startPolling,
    stopPolling,
    error: isAbortError(error) ? null : error,
    ...rest,
  };
};
